// src/redux/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";
import { jwtDecode } from "jwt-decode";
import { encryptPayload } from "../utils/crypto";

// Guard for non-browser environments (SSR / test)
const getValidStoredToken = () => {
  if (typeof window === "undefined") return null;
  const token = sessionStorage.getItem("accessToken");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp * 1000 < Date.now()) {
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
      return null;
    }
    return token;
  } catch {
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    return null;
  }
};

const accessToken = getValidStoredToken();
const refreshToken =
  typeof window !== "undefined" ? sessionStorage.getItem("refreshToken") : null;

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const encryptedPayload = encryptPayload(credentials);
      const response = await axiosInstance.post("/api/auth/login", {
        payload: encryptedPayload,
      });
      const token = response.data.token;
      const refreshToken = response.data.refreshToken;

      const decodedToken = jwtDecode(token);

      // sessionStorage writes belong in the thunk, not the reducer
      sessionStorage.setItem("accessToken", token);
      sessionStorage.setItem("refreshToken", refreshToken);

      return {
        token,
        refreshToken,
        user: response.data.user,
        role: decodedToken.role,
        userId: decodedToken.userId,
      };
    } catch (err) {
      // Pass the actual server error message so the UI can display it
      const message =
        err.response?.data?.message || err.message || "Login failed";
      return rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    role: null,
    token: accessToken || null,
    refreshToken: refreshToken || null,
    isAuthenticated: !!accessToken,
    error: null,
    userId: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.role = null;
      state.token = null;
      state.refreshToken = null;
      state.userId = null;
      state.isAuthenticated = false;
      // sessionStorage cleared here so a single dispatch(logout()) is sufficient
      // from any caller — axiosInstance, components, or token expiry handler
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
    },
    setUser: (state, action) => {
      state.user = action.payload.user;
      state.role = action.payload.role;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
    },
    setAccessToken: (state, action) => {
      state.token = action.payload;
      // Keep sessionStorage in sync so the next page load restores the token
      sessionStorage.setItem("accessToken", action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
        state.userId = action.payload.userId;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { logout, setUser, setAccessToken } = authSlice.actions;
export default authSlice.reducer;
